import { getGlobalHeadersAsObject } from "../global-headers";
import type { DRFListResponseWrapper } from "../types";

export type EmailMessageQueueSearchParams = {
	user__id?: number;
	person__id?: number;
	assignment__id?: number;
	from_template__name?: string;
	up_group?: number | "all";
	sent?: boolean;
	send_immediately?: boolean;
	manually?: boolean;
	error_isnull?: boolean;
	error_smtp_only?: boolean;
	limit?: number;
	offset?: number;
	ordering?: string;
};

export type EmailQueueV2 = {
	id?: number;
	created?: string;
	user: number;
	person: number;
	raw_text?: string;
	subject?: string;
	manually: boolean;
	from_template?: number;
	send_immediately?: boolean;
	in_reply_to?: number;
	sent?: boolean;
	sent_at?: string;
	error?: string | null;
	person_name?: string;
	person_url?: string;
	assignment_id?: number;
	assignment_name?: string;
	template_name?: string;
	/** Who wrote the pitch: the AI prompt version, else the template. */
	pitch_source_name?: string | null;
	recipient_email?: string | null;
	smtp_verified_status?: number | null;
	smtp_verified_at?: string | null;
	bounced?: boolean;
};

export type EmailResponse = {
	id: number;
	created: string;
	user: number;
	person: number;
	raw_text: string;
	sent: boolean;
	manually: boolean;
	from_template: number;
	recipient_email?: string;
};

type SendEmailArgs = {
	to: number;
	from: number;
	text: string;
	sendImmediately: boolean;
	fromTemplate?: number;
	subject?: string;
	inReplyTo?: number;
	personEmailId?: number;
};

export const addEmailToQueue = ({
	from,
	to,
	text,
	sendImmediately = false,
	fromTemplate,
	subject,
	inReplyTo,
	personEmailId,
}: SendEmailArgs): Promise<EmailResponse> => {
	return fetch("/api/v2/emailqueue/", {
		method: "POST",
		body: JSON.stringify({
			user: from,
			person: to,
			raw_text: text,
			manually: true,
			send_immediately: sendImmediately,
			from_template: fromTemplate,
			subject: subject,
			in_reply_to: inReplyTo,
			personemail: personEmailId,
		}),
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
	}).then((res) => {
		if (!res.ok) {
			throw new Error(`HTTP ${res.status}`);
		}
		return res.json();
	});
};

export const updateEmailOnQueue = (
	messageId: number,
	text: string
): Promise<EmailResponse> => {
	return fetch(`/api/v2/emailqueue/${messageId}/`, {
		method: "PATCH",
		body: JSON.stringify({ raw_text: text }),
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
	}).then((res) => res.json());
};

export const deleteEmailOnQueue = (messageId: number): Promise<any> => {
	return fetch(`/api/v2/emailqueue/${messageId}/`, {
		method: "DELETE",
		headers: {
			...getGlobalHeadersAsObject(),
		},
	});
};

export const bulkDeleteEmailQueue = (ids: number[]): Promise<{ deleted: number }> => {
	return fetch("/api/v2/emailqueue/bulk_delete/", {
		method: "POST",
		body: JSON.stringify({ ids }),
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
	}).then((res) => res.json());
};

export const getEmailBySender = (
	senderId: number,
	sent?: boolean
): Promise<EmailResponse[]> => {
	if (!senderId)
		throw new Error(
			"Required argument 'senderId' was not supplied to getEmailBySender"
		);

	let url = `/api/v2/emailqueue/?user__id=${senderId}`;
	if (sent) url += `&sent=${sent}`;

	return fetch(url).then((res) => res.json());
};

export const getEmailByReceiver = (
	receiverId: number,
	sent?: boolean
): Promise<EmailResponse[]> => {
	if (!receiverId)
		throw new Error(
			"Required argument 'receiverId' was not supplied to getEmailByReceiver"
		);

	let url = `/api/v2/emailqueue/?person__id=${receiverId}`;
	if (sent) url += `&sent=${sent}`;

	return fetch(url).then((res) => res.json());
};

export type PersonEmailQueueResponse = EmailResponse & {
	email_journal_id?: number;
	user_name: string;
	user_first_name?: string;
	recipient_email?: string;
	assignment_id?: number;
	assignment_name?: string;
	template_name?: string;
	/** Who wrote the pitch: the AI prompt version, else the template. */
	pitch_source_name?: string | null;
	vote_id?: number;
	sent_at?: string;
	subject?: string;
	has_tracking?: boolean;
	opened?: boolean;
	error?: string | null;
	bounced?: boolean;
	computed_url?: string | null;
};

export const getEmailQueueForPerson = (
	personId: number,
	options?: { includeSent?: boolean }
): Promise<PersonEmailQueueResponse[]> => {
	if (!personId) {
		throw new Error(
			"Required argument 'personId' was not supplied to getEmailQueueForPerson"
		);
	}

	let url = `/api/v2/persons/${personId}/email_queue/`;
	if (options?.includeSent) {
		url += "?include_sent=true";
	}
	return fetch(url).then((res) => res.json());
};

export type QueueAssignment = {
	id: number;
	name: string;
	group_name: string | null;
	count: number;
};

export type QueueUser = {
	id: number;
	username: string;
	count: number;
};

export type QueueTemplate = {
	name: string;
	count: number;
};

export const getEmailQueueDistinctUsers = (
	params?: Partial<EmailMessageQueueSearchParams>
): Promise<QueueUser[]> => {
	const searchParams = new URLSearchParams();
	if (params) {
		for (const [key, val] of Object.entries(params)) {
			if (val != null) searchParams.set(key, String(val));
		}
	}
	const qs = searchParams.toString();
	return fetch(`/api/v2/emailqueue/distinct_users/${qs ? `?${qs}` : ""}`, {
		headers: getGlobalHeadersAsObject(),
	}).then((res) => res.json());
};

export const getEmailQueueDistinctAssignments = (
	params?: Partial<EmailMessageQueueSearchParams>
): Promise<QueueAssignment[]> => {
	const searchParams = new URLSearchParams();
	if (params) {
		for (const [key, val] of Object.entries(params)) {
			if (val != null) searchParams.set(key, String(val));
		}
	}
	const qs = searchParams.toString();
	return fetch(`/api/v2/emailqueue/distinct_assignments/${qs ? `?${qs}` : ""}`, {
		headers: getGlobalHeadersAsObject(),
	}).then((res) => res.json());
};

export const getEmailQueueDistinctTemplates = (
	params?: Partial<EmailMessageQueueSearchParams>
): Promise<QueueTemplate[]> => {
	const searchParams = new URLSearchParams();
	if (params) {
		for (const [key, val] of Object.entries(params)) {
			if (val != null) searchParams.set(key, String(val));
		}
	}
	const qs = searchParams.toString();
	return fetch(`/api/v2/emailqueue/distinct_templates/${qs ? `?${qs}` : ""}`, {
		headers: getGlobalHeadersAsObject(),
	}).then((res) => res.json());
};

export const getEmailQueue = (
	params: EmailMessageQueueSearchParams
): Promise<DRFListResponseWrapper<EmailQueueV2[]>> => {
	const queryParams = Object.keys(params).map((key) => {
		return [key, params[key]];
	});
	return fetch(`/api/v2/emailqueue/?${new URLSearchParams(queryParams).toString()}`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then(async (res) => {
		if (res.ok) return res.json();
		throw new Error(await res.json());
	});
};
